//
// Copyright 2026 DXOS.org
//

import * as Cause from 'effect/Cause';
import * as Context from 'effect/Context';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import type * as EffectRpc from 'effect/rpc/Rpc';
import * as RpcGroup from 'effect/rpc/RpcGroup';
import * as RpcSchema from 'effect/rpc/RpcSchema';
import * as RpcServer from 'effect/rpc/RpcServer';
import * as RpcTest from 'effect/rpc/RpcTest';
import type * as Scope from 'effect/Scope';
import * as Stream from 'effect/Stream';

import { Stream as PbStream } from '@dxos/async';
import * as EffectEx from '@dxos/effect/EffectEx';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { makeInProcessClient, normalizeHandlers, runServiceEffect, toServiceError } from '@dxos/protocols';
import {
  BlobStoreService,
  ContactsService,
  DataService,
  DevicesService,
  DevtoolsHost,
  EdgeAgentService,
  FeedService,
  IdentityService,
  InboxService,
  InvitationsService,
  LoggingService,
  NetworkService,
  QueryService,
  SpacesService,
  SqlService,
  SystemService,
} from '@dxos/protocols/rpc';
import { type RequestOptions } from '@dxos/protocols/service-contract';
import { type RpcPort, RpcRouter, layerProtocolRpcPortClient, layerProtocolRpcPortServer } from '@dxos/rpc';
import { createIFramePort } from '@dxos/rpc-tunnel';

import { DEFAULT_CLIENT_CHANNEL } from './config.ts';
import * as Rpc from './Rpc.ts';
import { type ClientServices } from './service.ts';

export type MessagePortLike = MessagePort;

/**
 * Transport a {@link ClientServicesRpc} can run over: the native Worker-platform {@link MessagePort}
 * (shared-worker app port) or a byte {@link RpcPort} for legacy protobuf bridges (iframe shell,
 * devtools extension).
 */
export type ClientServicesTransport = MessagePortLike | RpcPort;

/**
 * All client service RPCs served over a single connection.
 * Rpc tags are prefixed with the {@link ClientServices} key (e.g. `DataService.subscribe`).
 */
export class ClientServicesRpcs extends RpcGroup.make().merge(
  SystemService.Rpcs,
  NetworkService.Rpcs,
  LoggingService.Rpcs,
  IdentityService.Rpcs,
  InvitationsService.Rpcs,
  DevicesService.Rpcs,
  SpacesService.Rpcs,
  DataService.Rpcs,
  QueryService.Rpcs,
  FeedService.Rpcs,
  ContactsService.Rpcs,
  InboxService.Rpcs,
  EdgeAgentService.Rpcs,
  SqlService.Rpcs,
  BlobStoreService.Rpcs,
  DevtoolsHost.Rpcs,
) {}

type ClientServicesRpcUnion = RpcGroup.Rpcs<typeof ClientServicesRpcs>;

/**
 * Host-side service implementations, one per client service, each in the effect-rpc `Handlers`
 * shape (Effect/Stream-returning, tag-keyed). This is the shape service hosts provide and
 * {@link ClientRpcServer} serves directly, with no protobuf encode/decode at the boundary.
 */
export type ClientServicesHandlers = {
  SystemService: SystemService.Handlers;
  NetworkService: NetworkService.Handlers;
  LoggingService: LoggingService.Handlers;
  IdentityService: IdentityService.Handlers;
  InvitationsService: InvitationsService.Handlers;
  DevicesService: DevicesService.Handlers;
  SpacesService: SpacesService.Handlers;
  DataService: DataService.Handlers;
  QueryService: QueryService.Handlers;
  FeedService: FeedService.Handlers;
  ContactsService: ContactsService.Handlers;
  InboxService: InboxService.Handlers;
  EdgeAgentService: EdgeAgentService.Handlers;
  SqlService: SqlService.Handlers;
  BlobStoreService: BlobStoreService.Handlers;
  DevtoolsHost: DevtoolsHost.Handlers;
};

const isVoidSchema = (schema: { ast: { _tag: string } }): boolean => schema.ast._tag === 'Void';

/** Splits an rpc tag into the {@link ClientServices} key and the service method name. */
const parseTag = (tag: string): [serviceKey: keyof ClientServices, methodName: string] => {
  const index = tag.indexOf('.');
  return [tag.slice(0, index) as keyof ClientServices, tag.slice(index + 1)];
};

/** Resolves a bound service method from a services provider, throwing if the service/method is absent. */
const resolveServiceMethod = (
  services: Partial<ClientServices>,
  serviceKey: keyof ClientServices,
  methodName: string,
  tag: string,
): ((request: unknown) => unknown) => {
  const service = services[serviceKey] as Record<string, unknown> | undefined;
  if (!service) {
    throw new Error(`Service not available: ${serviceKey}`);
  }
  const method = service[methodName];
  if (typeof method !== 'function') {
    throw new Error(`Method not available: ${tag}`);
  }
  return (method as (request: unknown) => unknown).bind(service);
};

//
// Server.
//

export type ClientRpcServerParams = {
  /**
   * Native Worker-platform transport. Provide this or {@link protocol}.
   */
  port?: MessagePortLike;
  /**
   * Pre-built server protocol (the value the {@link RpcServer.Protocol} tag resolves to — e.g. handed
   * to a worker-framework session via effect context). Provide this or {@link port}.
   */
  protocol?: RpcServer.Protocol['Service'];
  /**
   * Resolved per call so the served set follows the host lifecycle (services host open/close).
   */
  services: () => Partial<ClientServicesHandlers>;
  /**
   * Awaited before dispatching each request (e.g. worker readiness); a rejection fails the call.
   */
  onRequest?: () => Promise<void>;
};

/**
 * Serves {@link ClientServices} implementations over a {@link MessagePort} via effect-rpc.
 */
export class ClientRpcServer {
  readonly #params: ClientRpcServerParams;
  #server?: ReturnType<typeof Rpc.serve>;

  constructor(params: ClientRpcServerParams) {
    this.#params = params;
  }

  async open(): Promise<void> {
    if (this.#server) {
      return;
    }

    const handlers = makeClientServicesHandlers(this.#params);
    // `timing` publishes dxos.rpc.queueWait/service.duration. Queue wait is the only signal that
    // shows the worker being saturated rather than slow, and it is measurable only from the client
    // side of the port. Both ends must agree: the middleware is `requiredForClient`, so a server
    // that applies it while the client does not would reject every request — see
    // `makeClientServicesRpc` below.
    const options = { disableTracing: true, concurrency: 'unbounded', timing: true } as const;
    if (this.#params.protocol) {
      this.#server = Rpc.serveOverProtocol(
        Layer.succeed(RpcServer.Protocol, this.#params.protocol),
        ClientServicesRpcs,
        handlers,
        options,
      );
    } else {
      invariant(this.#params.port, 'ClientRpcServer requires a port or a protocol.');
      this.#server = Rpc.serve(this.#params.port, ClientServicesRpcs, handlers, options);
    }
    await this.#server.open();
  }

  async close(): Promise<void> {
    const server = this.#server;
    this.#server = undefined;
    await server?.close();
  }
}

export type ServeClientServicesOverIFrameOptions = {
  iframe: HTMLIFrameElement;
  origin: string;
  channel?: string;
  services: () => Partial<ClientServicesHandlers>;
};

/**
 * Serves {@link ClientServices} to a child iframe (e.g. the shell) over the effect-rpc byte protocol
 * and opens the connection. The iframe uses a byte {@link RpcPort} transport (not a `MessagePort`),
 * matching the shell's {@link ClientServicesProxy} consumer, so this serves over
 * {@link layerProtocolRpcPortServer} rather than the Worker-runner protocol used by
 * {@link ClientRpcServer}. Kept here — alongside the rpc definitions — so the `RpcPort` binding is
 * resolved in this package's type universe rather than in `@dxos/client`, whose merged-with-`main`
 * build resolves `ClientRpcServer.port` as a `MessagePort`.
 */
export const serveClientServicesOverIFrame = async ({
  iframe,
  origin,
  channel = DEFAULT_CLIENT_CHANNEL,
  services,
}: ServeClientServicesOverIFrameOptions): Promise<Rpc.GroupServer> => {
  const server = Rpc.serveOverProtocol(
    layerProtocolRpcPortServer(createIFramePort({ channel, iframe, origin })),
    ClientServicesRpcs,
    makeClientServicesHandlers({ services }),
    { disableTracing: true, concurrency: 'unbounded' },
  );
  await server.open();
  return server;
};

/**
 * The rpc tag prefix a group was defined with (e.g. `DataService.`), read off its requests so a
 * registration carries no second copy of the service name.
 */
const servicePrefix = <Rpcs extends EffectRpc.Any>(group: RpcGroup.RpcGroup<Rpcs>): string => {
  const [tag] = [...group.requests.keys()];
  invariant(tag, 'rpc group has no requests');
  const index = tag.indexOf('.');
  invariant(index > 0, `rpc tag is not service-prefixed: ${tag}`);
  return tag.slice(0, index + 1);
};

/**
 * Registers a service's handlers with the stack's {@link RpcRouter.RpcRouter}, which serves them
 * over every attached transport (and in-process) for the life of the layer. Handler creation and
 * registration stay separate layers: a provider merges this with the layer that supplies its tag,
 * and no list of services is needed anywhere else.
 */
export const RegisterService = <Rpcs extends EffectRpc.Any, Identifier>(
  rpc: RpcGroup.RpcGroup<Rpcs>,
  tag: Context.Key<Identifier, RpcGroup.HandlersFrom<Rpcs>>,
): Layer.Layer<never, never, Identifier | RpcRouter.RpcRouter | EffectRpc.ServicesServer<Rpc.Served<Rpcs>>> =>
  Layer.effectDiscard(
    Effect.gen(function* () {
      const handlers = normalizeHandlers(rpc, yield* tag);
      yield* Rpc.serveOnRouter(servicePrefix(rpc), rpc, handlers, {
        // The in-process surface calls the handlers directly, so it is built from the group as
        // defined rather than from the middleware-wrapped one the transport serves.
        inProcessClient: makeInProcessClient(rpc, handlers),
      });
    }),
  );

/**
 * The whole in-process {@link ClientServicesRpc}, sourced from the services registered with the
 * router rather than from an enumerated handler map.
 */
export const makeClientServicesRpcFromRouter: Effect.Effect<
  ClientServicesRpc,
  never,
  RpcRouter.RpcRouter | Scope.Scope
> =
  // The router's client is keyed dynamically by rpc tag; `ClientServicesRpc` is the static view of
  // the same record, and is what every consumer of the stack expects.
  RpcRouter.client.pipe(Effect.map((client) => client as unknown as ClientServicesRpc));

/**
 * Builds handler layers for every client service RPC, dispatching to the service implementations
 * resolved from `services` on each call.
 */
export const makeClientServicesHandlers = ({
  services,
  onRequest,
}: Pick<ClientRpcServerParams, 'services' | 'onRequest'>): Layer.Layer<EffectRpc.ToHandler<ClientServicesRpcUnion>> => {
  const gate = onRequest ? Effect.tryPromise({ try: onRequest, catch: toServiceError }) : Effect.void;

  const handlers: Record<string, (payload: unknown) => unknown> = {};
  for (const [tag, rpc] of ClientServicesRpcs.requests) {
    const [serviceKey] = parseTag(tag);
    // The host service is itself in the Handlers shape, keyed by the full prefixed tag; invoking it
    // returns the Effect/Stream directly, so no protobuf encode/decode adapter is needed.
    // A missing handler is a TYPED failure, never a throw: the worker-pool client reads a defect as
    // the shared connection crashing and fails every unrelated in-flight call with it.
    const resolve = (): ((payload: unknown) => unknown) | Error => {
      const service = services()[serviceKey] as Record<string, (payload: unknown) => unknown> | undefined;
      const handler = service?.[tag];
      if (typeof handler !== 'function') {
        return new Error(`Service handler not available: ${tag}`);
      }
      return handler.bind(service);
    };

    if (RpcSchema.isStreamSchema(rpc.successSchema)) {
      handlers[tag] = (payload: unknown) =>
        gate.pipe(
          Effect.flatMap(() => {
            const handler = resolve();
            return handler instanceof Error
              ? Effect.fail(handler)
              : Effect.succeed(handler(payload) as Stream.Stream<unknown, unknown>);
          }),
          Stream.unwrap,
        );
    } else {
      handlers[tag] = (payload: unknown) =>
        gate.pipe(
          Effect.flatMap(() => {
            const handler = resolve();
            return handler instanceof Error
              ? Effect.fail(handler)
              : (handler(payload) as Effect.Effect<unknown, unknown>);
          }),
        );
    }
  }

  // Handlers are dispatched dynamically across all merged service groups, so their per-method types
  // cannot be expressed statically.
  return ClientServicesRpcs.toLayer(handlers as never);
};

//
// Client.
//

/**
 * Effect-native client for all client services, inferred from the effect-rpc definitions.
 * Keyed by the full rpc tag (e.g. `rpc['DataService.subscribe'](req)` returns a `Stream`, unary
 * methods return an `Effect`). Each service's `Client` type already carries its own prefixed tags,
 * so this is their intersection — cheaper for the type-checker than re-expanding the full merged
 * {@link RpcClient.RpcClient} mapped type over all services.
 */
export interface ClientServicesRpc
  extends
    SystemService.Client,
    NetworkService.Client,
    LoggingService.Client,
    IdentityService.Client,
    InvitationsService.Client,
    DevicesService.Client,
    SpacesService.Client,
    DataService.Client,
    QueryService.Client,
    FeedService.Client,
    ContactsService.Client,
    InboxService.Client,
    EdgeAgentService.Client,
    SqlService.Client,
    BlobStoreService.Client,
    DevtoolsHost.Client {}

/**
 * Builds the effect-native {@link ClientServicesRpc} over a {@link MessagePort}.
 * The returned scope owns the connection; closing it releases the transport.
 */
export const makeClientServicesRpc = (
  port: ClientServicesTransport,
): Effect.Effect<ClientServicesRpc, never, Scope.Scope> =>
  // An RpcPort (byte transport) carries the legacy iframe/devtools bridges; a MessagePort uses the
  // native Worker-platform protocol.
  ('send' in port
    ? Rpc.makeClientOverProtocol(layerProtocolRpcPortClient(port), ClientServicesRpcs, {
        disableTracing: true,
        timing: true,
      })
    : Rpc.makeClient(port, ClientServicesRpcs, { disableTracing: true, timing: true })
  ).pipe(Effect.map((client) => client as ClientServicesRpc));

/**
 * Builds an in-process {@link ClientServicesRpc} backed directly by host {@link ClientServicesHandlers}
 * (no wire hop or serialization). Used by host-internal consumers (diagnostics, devtools) that need
 * the client surface without a transport.
 */
export const makeInProcessClientServicesRpc = (
  services: () => Partial<ClientServicesHandlers>,
): Effect.Effect<ClientServicesRpc, never, Scope.Scope> =>
  RpcTest.makeClient(ClientServicesRpcs).pipe(
    Effect.provide(makeClientServicesHandlers({ services })),
  ) as unknown as Effect.Effect<ClientServicesRpc, never, Scope.Scope>;

/**
 * Derives host {@link ClientServicesHandlers} from an effect-native {@link ClientServicesRpc}, so a
 * client-side rpc surface can be re-served (e.g. the devtools bridge). Each handler delegates to the
 * corresponding client method.
 */
export const makeHandlersFromRpc = (rpc: ClientServicesRpc): Partial<ClientServicesHandlers> => {
  // The rpc client is a flat record keyed by the prefixed tag (`InvitationsService.queryInvitations`),
  // not a per-service object; the handler shape it is being reshaped into *is* nested by service.
  const rpcRecord = rpc as unknown as Record<string, (...args: any[]) => unknown>;
  const handlers: Partial<Record<keyof ClientServices, Record<string, unknown>>> = {};
  for (const [tag] of ClientServicesRpcs.requests) {
    const [serviceKey] = parseTag(tag);
    const service = (handlers[serviceKey] ??= {});
    service[tag] = (payload: unknown) => rpcRecord[tag](payload);
  }
  return handlers as Partial<ClientServicesHandlers>;
};

/**
 * Rewrites one stream rpc of a {@link makeDeferredClientServicesRpc} surface; `live` is the call against
 * the resolved services, which starts only once they are reachable.
 */
export type DeferredStreamInterceptor = (
  payload: unknown,
  live: Stream.Stream<unknown, unknown>,
) => Stream.Stream<unknown, unknown>;

/**
 * Builds a {@link ClientServicesRpc} whose every call waits for `resolve` and then runs against the rpc
 * it yields, so a provider can hand out its surface before the services behind it are reachable.
 * `resolve` is re-run per call, so a provider that replaces its rpc on reconnect stays current.
 */
export const makeDeferredClientServicesRpc = (
  resolve: Effect.Effect<ClientServicesRpc, unknown>,
  interceptors: Partial<Record<string, DeferredStreamInterceptor>> = {},
): ClientServicesRpc => {
  // Keyed by the prefixed tag, like the rpc clients this stands in for; the methods are addressed
  // dynamically from the rpc groups, so their per-method types cannot be expressed statically.
  const target = (rpc: ClientServicesRpc, tag: string) =>
    (rpc as unknown as Record<string, (payload: unknown) => unknown>)[tag];
  const deferred: Record<string, (payload: unknown) => unknown> = {};
  for (const [tag, rpcDef] of ClientServicesRpcs.requests) {
    if (RpcSchema.isStreamSchema(rpcDef.successSchema)) {
      const live = (payload: unknown) =>
        Stream.unwrap(Effect.map(resolve, (rpc) => target(rpc, tag)(payload) as Stream.Stream<unknown, unknown>));
      const intercept = interceptors[tag];
      deferred[tag] = intercept ? (payload) => intercept(payload, live(payload)) : live;
    } else {
      deferred[tag] = (payload) =>
        Effect.flatMap(resolve, (rpc) => target(rpc, tag)(payload) as Effect.Effect<unknown, unknown>);
    }
  }
  return deferred as unknown as ClientServicesRpc;
};

/**
 * Derives the Promise/{@link PbStream} shaped {@link ClientServices} from an effect-native
 * {@link ClientServicesRpc}. Retained for consumers not yet migrated to the effect surface.
 */
export const makeServicesFromRpc = (
  rpc: ClientServicesRpc,
  runtime: Context.Context<never>,
): Partial<ClientServices> => {
  // The rpc client is a flat record keyed by the prefixed tag; methods are addressed dynamically
  // from the rpc groups, so the per-method types cannot be expressed statically.
  const rpcRecord = rpc as unknown as Record<string, (...args: any[]) => unknown>;
  const services: Partial<Record<keyof ClientServices, Record<string, unknown>>> = {};
  for (const [tag, rpcDef] of ClientServicesRpcs.requests) {
    const [serviceKey, methodName] = parseTag(tag);
    const service = (services[serviceKey] ??= {});
    const hasPayload = !isVoidSchema(rpcDef.payloadSchema);
    const invoke = (request?: unknown) => rpcRecord[tag](hasPayload ? (request ?? {}) : undefined);

    if (RpcSchema.isStreamSchema(rpcDef.successSchema)) {
      service[methodName] = (request?: unknown) =>
        streamToPbStream(runtime, invoke(request) as Stream.Stream<unknown, unknown>, tag);
    } else {
      service[methodName] = (request?: unknown, options?: RequestOptions) =>
        runServiceEffect(runtime, invoke(request) as Effect.Effect<unknown, unknown, never>, {
          timeout: options?.timeout,
          label: tag,
        });
    }
  }
  return services as Partial<ClientServices>;
};

/**
 * Builds an effect-native {@link ClientServicesRpc} from Promise/{@link PbStream} shaped
 * {@link ClientServices} implementations, without a wire hop. Used by in-process providers
 * (e.g. `LocalClientServices`) so their consumers use the same effect surface as remote proxies.
 */
export const makeRpcFromServices = (services: () => Partial<ClientServices>): ClientServicesRpc => {
  const rpc: Record<string, Record<string, (...args: any[]) => unknown>> = {};
  for (const [tag, rpcDef] of ClientServicesRpcs.requests) {
    const [serviceKey, methodName] = parseTag(tag);
    const service = (rpc[serviceKey] ??= {});
    const resolveMethod = () => resolveServiceMethod(services(), serviceKey, methodName, tag);

    if (RpcSchema.isStreamSchema(rpcDef.successSchema)) {
      service[methodName] = (request?: unknown) =>
        pbStreamToStream(() => resolveMethod()(request) as PbStream<unknown>);
    } else {
      service[methodName] = (request?: unknown) =>
        Effect.tryPromise({ try: async () => resolveMethod()(request), catch: toServiceError });
    }
  }
  return rpc as unknown as ClientServicesRpc;
};

//
// Stream adapters.
//

/**
 * Adapts a protobuf service stream to an effect stream.
 * Unbounded buffering matches the push semantics of the source stream.
 */
export const pbStreamToStream = <T>(open: () => PbStream<T>): Stream.Stream<T, Error> =>
  EffectEx.streamFromEmitter<T, Error>((emit) => {
    let source: PbStream<T>;
    try {
      source = open();
    } catch (err) {
      emit.fail(toServiceError(err));
      return;
    }
    source.subscribe(
      (data) => void emit.single(data),
      (err) => void (err ? emit.fail(toServiceError(err)) : emit.end()),
    );
    return Effect.promise(async () => source.close());
  });

/**
 * Adapts an effect stream to a protobuf service stream.
 * Consumer close interrupts the underlying rpc subscription.
 */
export const streamToPbStream = <T>(
  runtime: Context.Context<never>,
  stream: Stream.Stream<T, unknown>,
  label?: string,
): PbStream<T> =>
  new PbStream<T>(({ ready, next, close }) => {
    const fiber = stream.pipe(
      Stream.onStart(Effect.sync(ready)),
      Stream.runForEach((item) => Effect.sync(() => next(item))),
      Effect.matchCauseEffect({
        onFailure: (cause) =>
          Effect.sync(() => {
            const error = toServiceError(Cause.squash(cause));
            if (Cause.hasInterruptsOnly(cause)) {
              close();
              return;
            }
            // A `PbStream` consumer may pass no error handler, in which case the failure is dropped
            // and the consumer waits forever — a silently unencodable response field once made the
            // app unbootable this way. Logged here so every client service stream is diagnosable.
            log.warn('client service stream failed', { label, error: String(error) });
            close(error);
          }),
        onSuccess: () => Effect.sync(() => close()),
      }),
      Effect.runForkWith(runtime),
    );

    return () => {
      fiber.interruptUnsafe();
    };
  });
